<template>
  <div class="finance-page">
    <h2 class="page-title">资金明细</h2>

    <!-- 选项卡导航 -->
    <div class="tab-nav">
      <button class="tab-btn" :class="{ active: activeTab === 'summary' }" @click="handleTabChange('summary')">
        收支汇总
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'sell' }" @click="handleTabChange('sell')">
        出售记录
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'buy' }" @click="handleTabChange('buy')">
        购买记录
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'recharge' }" @click="handleTabChange('recharge')">
        充值记录
      </button>
      <!-- <button class="tab-btn" :class="{ active: activeTab === 'withdraw' }" @click="handleTabChange('withdraw')">
        提现记录
      </button> -->
      <button class="tab-btn" :class="{ active: activeTab === 'refund' }" @click="handleTabChange('refund')">
        退款记录
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'otherIncome' }" @click="handleTabChange('otherIncome')">
        其他收入
      </button>
      <button class="tab-btn" :class="{ active: activeTab === 'otherExpense' }" @click="handleTabChange('otherExpense')">
        其他支出
      </button>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-area">
      <div class="date-range-group">
        <input type="date" class="date-input" v-model="startDate" placeholder="开始日期">
        <span class="date-separator">至</span>
        <input type="date" class="date-input" v-model="endDate" placeholder="结束日期">
      </div>
      <button class="filter-btn" @click="fetchData">搜索</button>
    </div>

    <!-- 交易表格 -->
    <div class="transaction-table">
      <table>
        <!-- 表头：动态列宽度 -->
        <thead>
          <tr>
            <!-- 交易时间列：全局固定120px -->
            <th class="col-time">交易时间</th>
            <!-- 模型信息列：仅4列表格显示，固定280px -->
            <th v-if="['summary', 'sell', 'buy', 'refund'].includes(activeTab)" class="col-model">模型信息</th>
            <!-- 交易信息列：动态宽度（4列=120px，3列=400px） -->
            <th class="col-trade" :style="{ width: getTradeColWidth() }">交易信息</th>
            <!-- 金额列：全局固定120px -->
            <th class="col-amount">金额 (云币)</th>
          </tr>
        </thead>
        <tbody>
          <!-- 空数据提示 -->
          <tr v-if="transactions.length === 0">
            <td :colspan="['summary', 'sell', 'buy', 'refund'].includes(activeTab) ? 4 : 3" class="empty-tip">
              暂无{{ tabNameMap[activeTab] }}记录
            </td>
          </tr>

          <!-- 数据行 -->
          <tr v-for="item in transactions" :key="item.id" class="transaction-row">
            <!-- 交易时间列 -->
            <td class="col-time">{{ formatTime(item.time) }}</td>

            <!-- 模型信息列 -->
            <td v-if="['summary', 'sell', 'buy', 'refund'].includes(activeTab)" class="col-model">
              <template v-if="['buy', 'sell'].includes(activeTab) || (activeTab === 'summary' && ['购买记录', '出售记录'].includes(item.orderType))">
                <div class="transaction-info-card">
                  <img :src="item.previewUrl || 'https://picsum.photos/seed/default/50/50'" alt="模型图片" class="model-img">
                  <div class="info-content">
                    <div class="info-title">{{ item.title || '未命名模型' }}</div>
                    <div class="info-tag">标签：{{ item.tag || '无' }}</div>
                    <div class="info-role">
                      {{ item.orderType === '购买记录' ? '作者：' : (item.orderType === '出售记录' ? '购买者：' : (activeTab === 'buy' ? '作者：' : '购买者：')) }}
                      {{ item.userName || '未知' }}
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="simple-model-info">无</div>
              </template>
            </td>

            <!-- 交易信息列 -->
            <td class="col-trade">【{{ item.orderType || tabNameMap[activeTab] }}】</td>

            <!-- 金额列：购买记录强制红色 -->
            <td class="col-amount" :class="getAmountClass(item)">
              {{ getAmountSymbol(item) }}{{ Number(item.amount).toFixed(2) }}云币
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页组件 - 与参考代码样式一致，每页8条 -->
    <div class="pagination-container" v-if="total > 0">
      <el-pagination @current-change="handleCurrentChange" :current-page="pageIndex" :page-size="pageSize"
        layout="total, prev, pager, next, jumper" :total="total" background>
      </el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FinancePage',
  data() {
    return {
      activeTab: 'summary', // 默认选中收支汇总
      startDate: '',
      endDate: '',
      transactions: [],
      // 选项卡名称映射
      tabNameMap: {
        summary: '收支汇总',
        sell: '出售记录',
        buy: '购买记录',
        recharge: '充值记录',
        withdraw: '提现记录',
        refund: '退款记录',
        otherIncome: '其他收入',
        otherExpense: '其他支出'
      },
      // 分页相关参数 - 每页8条
      pageIndex: 1, // 当前页码，默认第一页
      pageSize: 8,  // 每页条数，设置为8条
      total: 0      // 总数据条数
    }
  },
  mounted() {
    // 初始加载收支汇总数据
    this.fetchData();
  },
  methods: {
    // 格式化时间：YYYY-MM-DD HH:mm（缩短显示）
    formatTime(timeStr) {
      if (!timeStr) return '暂无时间';
      const date = new Date(timeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hour}:${minute}`;
    },

    // 选项卡切换：切换后立即查询对应数据，重置页码
    handleTabChange(tabKey) {
      this.activeTab = tabKey;
      this.pageIndex = 1; // 切换标签时重置为第一页
      this.fetchData();
    },

    // 获取交易信息列宽度
    getTradeColWidth() {
      return ['summary', 'sell', 'buy', 'refund'].includes(this.activeTab) ? '120px' : '400px';
    },

    // 获取金额列样式类（核心：购买记录强制红色）
    getAmountClass(item) {
      // 收支汇总的购买记录强制支出（红色）
      if (this.activeTab === 'summary' && item.orderType === '购买记录') {
        return 'expense';
      }
      return item.type === 'income' ? 'income' : 'expense';
    },

    // 获取金额符号（核心：购买记录强制减号）
    getAmountSymbol(item) {
      if (this.activeTab === 'summary' && item.orderType === '购买记录') {
        return '-'; // 购买记录强制减号
      }
      return item.type === 'income' ? '+' : '-';
    },

    // 分页 - 当前页码改变
    handleCurrentChange(val) {
      this.pageIndex = val; // 更新当前页码
      this.fetchData();    // 重新请求数据
    },

    // 获取数据：添加分页参数，每页8条
    fetchData() {
      // 映射选项卡到接口需要的orderType
      const orderTypeMap = {
        summary: '收支汇总',
        sell: '出售记录',
        buy: '购买记录',
        recharge: '充值记录',
        withdraw: '提现记录',
        refund: '退款记录',
        otherIncome: '其他收入',
        otherExpense: '其他支出'
      };

      // 构造接口参数 - 添加分页参数
      const params = {
        orderType: orderTypeMap[this.activeTab],
        start: this.startDate,
        end: this.endDate,
        pageIndex: this.pageIndex,  // 分页参数：当前页码
        pageSize: this.pageSize     // 分页参数：每页8条
      };

      // 清空当前数据，避免加载中显示旧数据
      this.transactions = [];

      // 调用接口查询数据
      this.$apiFun.getIncomeExpense(params)
        .then(res => {
          if (res.status === 200) {
            // 解析返回数据
            this.transactions = res.obj.map(item => {
              // 判定收支类型
              let isIncome = false;
              switch (this.activeTab) {
                case 'sell':
                case 'recharge':
                case 'otherIncome':
                  isIncome = true;
                  break;
                case 'buy':
                case 'withdraw':
                case 'refund':
                case 'otherExpense':
                  isIncome = false;
                  break;
                default: // 收支汇总：按金额判定
                  isIncome = Number(item.amountPaid || item.amount) > 0;
              }

              return {
                id: item.id || Math.random(), // 保证key唯一
                time: item.paymentTime || item.tradeTime || item.createTime || '',
                title: item.title || '', // 模型标题
                tag: item.fileFormat?.[0] || item.tag || '', // 模型标签
                previewUrl: item.previewUrl || '', // 模型图片
                userName: item.userName || item.operatorName || '', // 变动人
                orderType: item.orderType || '', // 接口返回的交易类型
                amount: Number(item.amountPaid || item.amount || 0), // 交易金额
                type: isIncome ? 'income' : 'expense' // 收支类型
              };
            });
            // 设置总条数（从res.num获取，与参考代码一致）
            this.total = res.num || 0;
          } else {
            this.$message.error(res.msg || `获取${this.tabNameMap[this.activeTab]}失败`);
          }
        })
        .catch(err => {
          console.error(`获取${this.tabNameMap[this.activeTab]}失败:`, err);
          this.$message.error(`获取${this.tabNameMap[this.activeTab]}数据异常`);
        });
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题变量
@bg-primary: #1a1a1a;
@bg-secondary: #2c2c2c;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-red: #f53f3f;
@accent-green: #00b42a;
@border-color: #444;
@input-bg: #333;
@hover-bg: #3a3a3a;
@accent-blue: #0ea5e9; // 分页组件主色

.finance-page {
  background-color: @bg-primary;
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;

  // 页面标题
  .page-title {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid @border-color;
  }

  // 选项卡导航
  .tab-nav {
    display: flex;
    gap: 2px;
    margin-bottom: 15px;
    flex-wrap: wrap;

    .tab-btn {
      background-color: @bg-secondary;
      border: none;
      padding: 10px 18px;
      font-size: 14px;
      color: @text-secondary;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 4px 4px 0 0;

      &.active {
        background-color: @bg-primary;
        color: @accent-red;
        border-bottom: 2px solid @accent-red;
        font-weight: 500;
      }

      &:hover:not(.active) {
        background-color: @hover-bg;
        color: @text-primary;
      }
    }
  }

  // 筛选区域
  .filter-area {
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;
    flex-wrap: wrap;
    background-color: @bg-secondary;
    padding: 12px;
    border-radius: 4px;

    .date-range-group {
      display: flex;
      align-items: center;
      gap: 8px;

      .date-input {
        background-color: @input-bg;
        border: 1px solid @border-color;
        color: @text-primary;
        padding: 8px 12px;
        border-radius: 4px;
        font-size: 13px;
        width: 140px;
        box-sizing: border-box;

        &:focus {
          outline: none;
          border-color: #409eff;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
        }
      }

      .date-separator {
        font-size: 14px;
        color: @text-primary;
        width: 20px;
        text-align: center;
      }
    }

    .filter-select {
      background-color: @input-bg;
      border: 1px solid @border-color;
      color: @text-primary;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 13px;
      min-width: 100px;

      &:focus {
        outline: none;
        border-color: #409eff;
        box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
      }
    }

    .filter-btn {
      background-color: #409eff;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 13px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: #337ecc;
      }

      &:active {
        background-color: #2668b3;
      }
    }
  }

  // 交易表格
  .transaction-table {
    width: 100%;
    overflow-x: auto;
    margin-bottom: 20px; // 为分页组件留出空间

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed; // 固定表格布局，确保列宽生效

      // 表头样式
      th {
        background-color: @bg-secondary;
        color: @text-primary;
        font-weight: 500;
        padding: 10px 8px;
        text-align: left;
        border: 1px solid @border-color;
        font-size: 14px;
        white-space: nowrap;
      }

      // 数据行样式：统一行高
      .transaction-row {
        height: 80px;

        &:nth-child(even) {
          background-color: #2a2a2a;
        }

        &:hover {
          background-color: #333333;
        }
      }

      // 单元格通用样式
      td {
        padding: 8px;
        border: 1px solid @border-color;
        font-size: 13px;
        color: @text-primary;
        vertical-align: middle;
      }

      // 固定列宽样式（核心：保证交易时间/金额宽度全局一致）
      .col-time {
        width: 120px !important;
        min-width: 120px !important;
        max-width: 120px !important;
        text-align: center;
        font-size: 12px;
      }

      .col-model {
        width: 280px !important;
        min-width: 280px !important;
        max-width: 280px !important;
        padding-left: 10px;
      }

      .col-trade {
        text-align: center;
        font-size: 12px;
      }

      .col-amount {
        width: 120px !important;
        min-width: 120px !important;
        max-width: 120px !important;
        text-align: center;
        font-weight: 500;
        font-size: 13px;
      }

      // 模型卡片样式
      .transaction-info-card {
        display: flex;
        align-items: center;
        gap: 8px;

        .model-img {
          width: 50px;
          height: 50px;
          object-fit: cover;
          border-radius: 4px;
          border: 1px solid @border-color;
          background-color: @bg-secondary;
        }

        .info-content {
          .info-title {
            font-weight: 500;
            margin-bottom: 2px;
            max-width: 200px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            font-size: 12px;
          }

          .info-tag,
          .info-role {
            font-size: 11px;
            color: @text-secondary;
            margin-bottom: 1px;
          }
        }
      }

      // 无模型信息样式
      .simple-model-info {
        font-size: 13px;
        color: @text-secondary;
        line-height: 80px;
        text-align: center;
      }

      // 空数据提示
      .empty-tip {
        text-align: center;
        color: @text-secondary;
        padding: 40px 0;
        font-size: 14px;
        height: 80px;
        line-height: 80px;
      }

      // 收支颜色
      .income {
        color: @accent-green;
      }

      .expense {
        color: @accent-red;
      }
    }
  }

  // 分页组件样式 - 与参考代码完全一致
  .pagination-container {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
  }

  // 核心：修改分页组件的背景色、字体色，适配深色主题
  :deep(.el-pagination) {
    background-color: transparent !important;

    .el-pagination__total {
      color: #e0e0e0 !important;
      font-size: 14px !important;
    }

    .el-pager {
      li {
        background-color: #080a12 !important;
        color: #a0a0a0 !important;
        border: 1px solid #424242 !important;
        margin: 0 2px !important;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: @accent-blue !important;
          border-color: @accent-blue !important;
        }

        &.active {
          background-color: @accent-blue !important;
          color: #ffffff !important;
          border-color: @accent-blue !important;
        }
      }
    }

    .btn-prev,
    .btn-next {
      background-color: @bg-secondary !important;
      color: @text-secondary !important;
      border: 1px solid @border-color !important;
      min-width: 30px !important;
      margin: 0 2px !important;
      
      &.disabled {
        background-color: darken(@bg-secondary, 5%) !important;
        color: darken(@text-secondary, 20%) !important;
        border-color: darken(@border-color, 10%) !important;
        cursor: not-allowed !important;
        
        &:hover {
          background-color: darken(@bg-secondary, 5%) !important;
          color: darken(@text-secondary, 20%) !important;
          border-color: darken(@border-color, 10%) !important;
        }
      }
      
      &:not(.disabled):hover {
        background-color: @hover-bg !important;
        color: @accent-blue !important;
        border-color: @accent-blue !important;
      }
      
      .el-icon {
        font-size: 12px !important;
        font-weight: bold !important;
      }
    }

    .el-pagination__jump {
      color: #a0a0a0 !important;

      .el-input__inner {
        background-color: #080a12 !important;
        color: #e0e0e0 !important;
        border: 1px solid #424242 !important;

        &:focus {
          border-color: @accent-blue !important;
        }
      }
    }
  }
}
</style>